import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const MatchStats = ({ statistics }) => {
  const [showDetailed, setShowDetailed] = useState(false);

  if (!statistics) return null;

  const StatCard = ({ icon, label, value, subtitle, color = "text-primary" }) => (
    <div className="bg-muted/30 rounded-lg p-3 text-center">
      <div className={`w-8 h-8 mx-auto mb-2 rounded-full bg-muted flex items-center justify-center`}>
        <Icon name={icon} size={16} className={color} />
      </div>
      <div className="text-lg font-bold text-card-foreground">
        {value}
      </div>
      <div className="text-xs text-muted-foreground">
        {label}
      </div>
      {subtitle && (
        <div className="text-xs text-muted-foreground mt-1">
          {subtitle}
        </div>
      )}
    </div>
  );

  const ProgressBar = ({ label, value, max, color = "bg-primary" }) => {
    const percentage = (value / max) * 100;
    
    return (
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm text-muted-foreground">{label}</span>
          <span className="text-sm font-medium text-card-foreground">
            {value}/{max}
          </span>
        </div>
        <div className="w-full bg-muted/50 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${color}`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold text-card-foreground">
          Estatísticas da Pelada
        </h3>
        
        <Button
          variant="ghost"
          size="sm"
          iconName={showDetailed ? 'ChevronUp' : 'ChevronDown'}
          onClick={() => setShowDetailed(!showDetailed)}
        />
      </div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatCard
          icon="Calendar"
          label="Peladas realizadas"
          value={statistics?.totalMatches}
          color="text-primary"
        />
        
        <StatCard
          icon="Users"
          label="Jogadores regulares"
          value={statistics?.regularPlayers}
          color="text-success"
        />
        
        <StatCard
          icon="TrendingUp"
          label="Média presença"
          value={statistics?.averageAttendance}
          subtitle="jogadores"
          color="text-info"
        />
        
        <StatCard
          icon="Trophy"
          label="Taxa vitórias"
          value={`${statistics?.winRate}%`}
          color="text-warning"
        />
      </div>

      {/* Last Match Result */}
      <div className="p-3 bg-muted/30 rounded-lg mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="Zap" size={16} className="text-success" />
            <span className="text-sm font-medium text-card-foreground">
              Último resultado
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-lg font-bold text-card-foreground">
              {statistics?.lastMatchScore}
            </span>
            <Icon name="Trophy" size={16} className="text-warning" />
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Vitória no último jogo • Sequência de 3 vitórias
        </p>
      </div>

      {/* Detailed Statistics */}
      {showDetailed && (
        <div className="space-y-4 pt-4 border-t border-border">
          {/* Attendance Progress */}
          <div>
            <h4 className="font-medium text-card-foreground mb-3 flex items-center">
              <Icon name="Users" size={16} className="mr-2 text-primary" />
              Histórico de Participação
            </h4>
            
            <ProgressBar
              label="Jogadores regulares (5+ peladas)"
              value={statistics?.regularPlayers}
              max={statistics?.totalMatches}
              color="bg-success"
            />
            
            <ProgressBar
              label="Média de comparecimento"
              value={statistics?.averageAttendance}
              max={22}
              color="bg-info"
            />
            
            <ProgressBar
              label="Taxa de retenção"
              value={85}
              max={100}
              color="bg-warning"
            />
          </div>

          {/* Performance Metrics */}
          <div>
            <h4 className="font-medium text-card-foreground mb-3 flex items-center">
              <Icon name="BarChart3" size={16} className="mr-2 text-primary" />
              Desempenho
            </h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-muted/30 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <Icon name="Target" size={16} className="text-success" />
                  <span className="text-sm font-medium text-card-foreground">
                    Vitórias
                  </span>
                </div>
                <div className="text-xl font-bold text-success">
                  {Math.round((statistics?.winRate / 100) * statistics?.totalMatches)}
                </div>
                <div className="text-xs text-muted-foreground">
                  de {statistics?.totalMatches} jogos
                </div>
              </div>
              
              <div className="bg-muted/30 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <Icon name="Star" size={16} className="text-warning" />
                  <span className="text-sm font-medium text-card-foreground">
                    Avaliação
                  </span>
                </div>
                <div className="text-xl font-bold text-warning">
                  {statistics?.averageRating?.toFixed(1)}
                </div>
                <div className="text-xs text-muted-foreground">
                  média geral
                </div>
              </div>
            </div>
          </div>

          {/* Recent Trends */}
          <div>
            <h4 className="font-medium text-card-foreground mb-3 flex items-center">
              <Icon name="TrendingUp" size={16} className="mr-2 text-primary" />
              Tendências Recentes
            </h4>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between p-2 bg-success/10 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Icon name="ArrowUp" size={14} className="text-success" />
                  <span className="text-sm text-success">Participação</span>
                </div>
                <span className="text-sm font-medium text-success">+15%</span>
              </div>
              
              <div className="flex items-center justify-between p-2 bg-success/10 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Icon name="ArrowUp" size={14} className="text-success" />
                  <span className="text-sm text-success">Pontualidade</span>
                </div>
                <span className="text-sm font-medium text-success">+8%</span>
              </div>
              
              <div className="flex items-center justify-between p-2 bg-warning/10 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Icon name="ArrowDown" size={14} className="text-warning" />
                  <span className="text-sm text-warning">Cancelamentos</span>
                </div>
                <span className="text-sm font-medium text-warning">-12%</span>
              </div>
            </div>
          </div>

          {/* Top Performers */}
          <div>
            <h4 className="font-medium text-card-foreground mb-3 flex items-center">
              <Icon name="Crown" size={16} className="mr-2 text-primary" />
              Destaques
            </h4>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Icon name="Trophy" size={14} className="text-warning" />
                  <span className="text-sm text-card-foreground">Mais pontual</span>
                </div>
                <span className="text-sm font-medium text-card-foreground">João Silva</span>
              </div>
              
              <div className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Icon name="Users" size={14} className="text-info" />
                  <span className="text-sm text-card-foreground">Mais participativo</span>
                </div>
                <span className="text-sm font-medium text-card-foreground">Pedro Santos</span>
              </div>
              
              <div className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Icon name="Zap" size={14} className="text-success" />
                  <span className="text-sm text-card-foreground">Artilheiro</span>
                </div>
                <span className="text-sm font-medium text-card-foreground">Carlos Lima</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-3">
            <Button
              variant="outline"
              size="sm"
              iconName="BarChart3"
              iconPosition="left"
              className="flex-1"
              onClick={() => {
                // View detailed analytics
                console.log('View detailed analytics');
              }}
            >
              Ver Relatório Completo
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              iconPosition="left"
              className="flex-1"
              onClick={() => {
                // Export statistics
                console.log('Export statistics');
              }}
            >
              Exportar Dados
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchStats;